import { useState } from "react";
import { FileHeart, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const LEAFLETS = [
  { kind: "head_injury", label: "Head injury advice" },
  { kind: "child_head_injury", label: "Child head injury advice" },
  { kind: "wound_care", label: "Wound care advice" },
];
const LABELS = Object.fromEntries(LEAFLETS.map((l) => [l.kind, l.label]));

const fmtSent = (dt) =>
  dt ? new Date(dt).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

export default function AdviceSection({ patient, onChanged }) {
  const [sending, setSending] = useState("");
  const sent = patient.leaflets_sent || [];

  const send = async (kind) => {
    setSending(kind);
    try {
      const { data } = await api.post(`/patients/${patient.id}/leaflet`, { kind });
      toast.success(data.message);
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending("");
    }
  };

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6" data-testid="advice-section">
      <h2 className="font-heading text-lg font-bold text-[#0F2547] flex items-center gap-2 mb-1">
        <FileHeart className="h-5 w-5 text-[#007F3B]" /> Advice leaflets
      </h2>
      <p className="text-xs text-slate-500 mb-4">Tap to email the patient a standard advice leaflet — it sends straight away.</p>
      {!patient.email && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border-2 border-[#ED8B00] bg-amber-50 p-3" data-testid="leaflet-no-email-warning">
          <AlertTriangle className="h-4 w-4 text-[#ED8B00] shrink-0 mt-0.5" />
          <p className="text-xs font-semibold text-amber-800">
            No email address on this patient's record — add one in the patient details before sending a leaflet.
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {LEAFLETS.map((l) => (
          <button key={l.kind} onClick={() => send(l.kind)} disabled={Boolean(sending)} data-testid={`leaflet-${l.kind}`}
            className="h-12 px-3 rounded-lg bg-[#007F3B] hover:bg-[#006631] disabled:opacity-50 transition-colors text-white text-xs font-bold inline-flex items-center justify-center gap-1.5">
            <Send className="h-3.5 w-3.5" /> {sending === l.kind ? "Sending…" : l.label}
          </button>
        ))}
      </div>
      {sent.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-1.5" data-testid="leaflets-sent-list">
          {sent.map((s, i) => (
            <span key={i} data-testid={`leaflet-sent-${i}`}
              className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 text-[#007F3B] text-[11px] font-bold px-2.5 py-1 rounded-full">
              <CheckCircle2 className="h-3 w-3" /> {LABELS[s.kind] || s.label} · {fmtSent(s.sent_at)}
            </span>
          ))}
        </div>
      )}
    </section>
  );
}
